'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';
import { Menu } from './Menu';

/* ═══════════════════════════════════════════════════
   SplitButton — primary action + dropdown alternatives
   APG: Button (primary) + Menu Button (trigger + menu)

   The menu surface is rendered via the shared Menu primitive.
   SplitButton owns trigger, open/close state, portal, and
   fixed positioning. Menu owns keyboard navigation within items.
   ═══════════════════════════════════════════════════ */

export type SplitButtonIntent =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'tinted'
  | 'theme';
export type SplitButtonSize = 'sm' | 'md' | 'lg';

export interface SplitButtonMenuItem {
  /** Text label for the menu item. */
  label: string;
  /** Fires when the menu item is activated. */
  onAction: () => void;
  /** Optional leading icon. */
  icon?: ReactNode;
  /** When true the item is non-interactive. */
  disabled?: boolean;
  /** When true the item uses destructive styling. */
  destructive?: boolean;
}

export interface SplitButtonProps extends UnsafeProps {
  /** Primary action label. */
  children: ReactNode;
  /** Fires when the primary action button is clicked. */
  onAction: () => void;
  /** Leading icon on the primary action. */
  leadingIcon?: ReactNode;
  /** Intent — applied to both zones. */
  intent?: SplitButtonIntent;
  /** Size — applied to both zones. */
  size?: SplitButtonSize;
  /** Disabled — both zones. */
  disabled?: boolean;
  /** Loading — primary zone only; menu trigger stays accessible. */
  loading?: boolean;
  /** Menu items. */
  menuItems: SplitButtonMenuItem[];
  /** Accessible label for the menu trigger. */
  menuLabel?: string;
  /** Additional class name. */
  className?: string;
}

function Spinner() {
  return (
    <span className="j-btn-spinner" aria-hidden="true">
      <svg width="1em" height="1em" viewBox="0 0 16 16" fill="none">
        <circle
          cx="8"
          cy="8"
          r="6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="28"
          strokeDashoffset="8"
        />
      </svg>
    </span>
  );
}

function ChevronDown() {
  return (
    <span className="j-split-btn-chevron" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
          d="M3 5l3 3 3-3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export const SplitButton = React.forwardRef<HTMLDivElement, SplitButtonProps>(function SplitButton(
  {
    children,
    onAction,
    leadingIcon,
    intent = 'secondary',
    size = 'md',
    disabled = false,
    loading = false,
    menuItems,
    menuLabel,
    className,
    unsafe_className,
    unsafe_style,
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const [menuStyle, setMenuStyle] = React.useState<React.CSSProperties>({
    position: 'fixed',
    visibility: 'hidden',
  });

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const pendingFocusRef = React.useRef<'first' | 'last' | null>(null);

  // Compute menu position from trigger bounding rect.
  // Right-aligns by default; flips to left-align if the menu
  // would overflow the left edge of the viewport.
  const updateMenuPosition = React.useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Uses fallback width on initial open; resize listener corrects later
    const menuWidth = menuRef.current?.offsetWidth ?? 180;
    const wouldOverflowLeft = rect.right - menuWidth < 0;
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: wouldOverflowLeft ? rect.left : rect.right,
      transform: wouldOverflowLeft ? 'none' : 'translateX(-100%)',
      visibility: 'visible',
    });
  }, []);

  // Focus the appropriate menu item after the portal renders
  React.useEffect(() => {
    if (!open || pendingFocusRef.current === null) return;
    const container = menuRef.current;
    if (!container) return;
    const enabledItems = Array.from(
      container.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)'),
    );
    if (enabledItems.length === 0) return;
    const target =
      pendingFocusRef.current === 'last' ? enabledItems[enabledItems.length - 1] : enabledItems[0];
    target?.focus();
    pendingFocusRef.current = null;
  }, [open, menuStyle]);

  // Open the menu and focus the appropriate item
  const openMenu = React.useCallback(
    (focusDir: 'first' | 'last' = 'first') => {
      pendingFocusRef.current = focusDir;
      setOpen(true);
      updateMenuPosition();
    },
    [updateMenuPosition],
  );

  const closeMenu = React.useCallback((returnFocus = true) => {
    setOpen(false);
    setMenuStyle({ position: 'fixed', visibility: 'hidden' });
    if (returnFocus && triggerRef.current?.isConnected) {
      triggerRef.current.focus();
    }
  }, []);

  // Close on click outside
  React.useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target)
      ) {
        closeMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeMenu]);

  // Reposition menu on scroll/resize while open
  React.useEffect(() => {
    if (!open) return;
    const reposition = () => updateMenuPosition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, updateMenuPosition]);

  const handlePrimaryClick = React.useCallback(() => {
    if (loading || disabled) return;
    onAction();
  }, [onAction, loading, disabled]);

  const handleTriggerClick = React.useCallback(() => {
    if (disabled) return;
    if (open) {
      closeMenu();
    } else {
      openMenu('first');
    }
  }, [disabled, open, openMenu, closeMenu]);

  const handleTriggerKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) openMenu('first');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (!open) openMenu('last');
      }
    },
    [disabled, open, openMenu],
  );

  const handleMenuClose = React.useCallback(
    (reason: 'escape' | 'tab' | 'select') => {
      // Return focus to trigger on Escape or item selection; let browser
      // handle natural tab order on Tab.
      closeMenu(reason !== 'tab');
    },
    [closeMenu],
  );

  const intentClass = `j-btn-${intent}`;
  const sizeClass = `j-btn-${size}`;

  return (
    <div
      ref={ref}
      role="group"
      aria-label={typeof children === 'string' ? `${children} actions` : 'Split button actions'}
      className={cx('j-split-btn', className, unsafe_className)}
      style={unsafe_style}
    >
      {/* Primary action zone — when loading, use aria-disabled (not HTML
         disabled) so AT still announces the button as "busy, Save" rather
         than skipping it entirely. */}
      <button
        type="button"
        className={cx('j-btn', 'j-split-btn-action', intentClass, sizeClass)}
        disabled={disabled}
        aria-disabled={loading || undefined}
        aria-busy={loading || undefined}
        onClick={handlePrimaryClick}
      >
        {loading ? <Spinner /> : leadingIcon && <span className="j-btn-icon">{leadingIcon}</span>}
        <span className="j-btn-label" style={loading ? { opacity: 0.7 } : undefined}>
          {children}
        </span>
      </button>

      {/* Divider */}
      <span className="j-split-btn-divider" aria-hidden="true" />

      {/* Menu trigger zone */}
      <button
        ref={triggerRef}
        type="button"
        className={cx('j-btn', 'j-split-btn-trigger', intentClass, sizeClass)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={menuLabel ?? 'More actions'}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
      >
        <ChevronDown />
      </button>

      {/* Menu dropdown — portal to document.body, position: fixed.
          Positioning stays in SplitButton; Menu owns keyboard nav. */}
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <Menu
            ref={menuRef}
            ariaLabel={menuLabel ?? 'More actions'}
            className="j-split-btn-menu"
            style={menuStyle}
            onClose={handleMenuClose}
          >
            {menuItems.map((item, index) => (
              <Menu.Item
                key={`${item.label}-${index}`}
                label={item.label}
                icon={item.icon}
                disabled={item.disabled}
                destructive={item.destructive}
                onClick={item.onAction}
              />
            ))}
          </Menu>,
          document.body,
        )}
    </div>
  );
});
